import React, { useEffect, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import SystemDataService from '../../Services/SystemDataService';
import { getOurWorkData } from '../../Public/our-work-data';
import useHandleRedirect from '../../Hooks/useHandleRedirect';

import GalleryViewSwitcher from '../Gallery/gallery-view-switcher-component';
import GalleryMasonry from '../Gallery/gallery-masonry-component';
import GalleryScrollingColumns from '../Gallery/gallery-scrolling-columns-component';
import GalleryLightbox from '../Gallery/gallery-lightbox-component';
import GallerySpecials from '../Gallery/gallery-specials-component';

function OurWorkSection() {
    const [galleryImages, setGalleryImages] = useState([]);
    const [specials, setSpecials] = useState([]);
    const [lightbox, setLightbox] = useState(null);
    const [viewMode, setViewMode] = useState('columns');

    const handleRedirect = useHandleRedirect();
    const { language = 'en', dir = 'ltr' } = useSelector((state) => state.systemData || {});
    const ui = useMemo(() => getOurWorkData(language).ui, [language]);

    // ── [1] Fetch gallery + specials ─────────────────────────────────
    useEffect(() => {
        let mounted = true;
        Promise.all([
            SystemDataService.getGalleryImages(),
            SystemDataService.getSpecialsImages(),
        ]).then(([gallery, specialsData]) => {
            if (!mounted) return;
            setGalleryImages(
                (gallery || []).map((item, idx) => ({
                    id:    item.id    || idx,
                    src:   item.url   || item.image || item,
                    title: item.title || '',
                }))
            );
            setSpecials(specialsData || []);
        });
        return () => { mounted = false; };
    }, []);

    // ── [2] Lightbox helpers ─────────────────────────────────────────
    const closeLightbox = () => setLightbox(null);
    const prevImage = () => setLightbox((i) => (i - 1 + galleryImages.length) % galleryImages.length);
    const nextImage = () => setLightbox((i) => (i + 1) % galleryImages.length);

    return (
        <div className="w-full bg-white" dir={dir}>

            {/* ── GALLERY SECTION ──────────────────────────────────────── */}
            <section className="w-full px-4 md:px-6 py-10 md:py-14">
                <div className="max-w-[1400px] mx-auto">

                    {/* Header + view switcher */}
                    <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
                        <div>
                            <h1
                                className="text-5xl md:text-7xl font-bold uppercase italic er leading-none"
                            >
                                <span className="text-primary-950">{ui.galleryTitle.split(' ')[0]}</span>{' '}
                                <span className="text-secondary-500">{ui.galleryTitle.split(' ').slice(1).join(' ')}</span>
                            </h1>
                            <p className="text-gray-400 font-medium mt-2 text-sm md:text-base">{ui.gallerySubtitle}</p>
                        </div>

                        {galleryImages.length > 0 && (
                            <GalleryViewSwitcher active={viewMode} onChange={setViewMode} />
                        )}
                    </div>

                    {/* Gallery view */}
                    {galleryImages.length === 0 ? (
                        <div className="py-20 text-center text-gray-300 font-black uppercase tracking-widest">
                            {ui.noImagesText}
                        </div>
                    ) : viewMode === 'masonry' ? (
                        <GalleryMasonry images={galleryImages} onImageClick={setLightbox} />
                    ) : (
                        <GalleryScrollingColumns
                            images={galleryImages}
                            columns={3}
                            height="h-[82vh]"
                            onImageClick={setLightbox}
                        />
                    )}
                </div>
            </section>

            {/* ── SPECIALS ─────────────────────────────────────────────── */}
            {/* <GallerySpecials
                specials={specials}
                ui={ui}
                onItemClick={(e) => handleRedirect(e, ui.claimOfferBtnLink)}
            /> */}

            {/* ── LIGHTBOX ─────────────────────────────────────────────── */}
            <GalleryLightbox
                images={galleryImages}
                index={lightbox}
                onClose={closeLightbox}
                onPrev={prevImage}
                onNext={nextImage}
            />
        </div>
    );
}

export default OurWorkSection;
